import { Box, Card, Checkbox, Heading, IconButton, Paragraph, Spinner, Table } from '@zillow/constellation-10';
import type {
  ListingCompDto,
  MonthlyPriceHistoryDto,
  PortfolioAnalyticsPropertyResponseDto,
} from '@zg-rentals/ts-customer-analytics-client';
import { makeTableConfig, TooltipHeader } from './MarketCompsTable.config';
import { IconDownloadFilled } from '@zillow/constellation-icons';
import ExcelJS from 'exceljs';
import { format } from 'date-fns';
import { useMemo, useState } from 'react';
import { cva } from '@/styled-system/css';

type MarketCompsTableProps = {
  comps: Array<ListingCompDto>;
  isLoading: boolean;
  selectedProperty: PortfolioAnalyticsPropertyResponseDto;
  checkedCompIds: Set<string>;
  onToggleComp: (listingAlias: string, isSelectedProperty: boolean) => void;
  viewsRankMap: Map<string, number>;
  leadsRankMap: Map<string, number>;
  priceHistoryMap: Record<string, Array<MonthlyPriceHistoryDto>> | undefined;
  isLoadingPriceHistory: boolean;
};

const headerCellRecipe = cva({
  base: {
    position: 'sticky',
    top: 0,
    backgroundColor: 'white',
    userSelect: 'none',
    border: 'none',
    outline: '1px solid #e2e2e2',
    outlineOffset: '-1px',
  },
  variants: {
    sortable: {
      true: { cursor: 'pointer', _hover: { backgroundColor: '#f5f5f5' } },
      false: { cursor: 'default' },
    },
    stickyLeft: {
      true: { left: '57px', zIndex: 3, boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' },
      false: { zIndex: 1 },
    },
  },
  defaultVariants: { sortable: false, stickyLeft: false },
});

const checkboxCellRecipe = cva({
  base: {
    width: '50px',
    position: 'sticky',
    left: 0,
    border: 'none',
    outline: '1px solid #e2e2e2',
    outlineOffset: '-1px',
  },
  variants: {
    highlight: {
      header: { top: 0, zIndex: 3, backgroundColor: 'white' },
      selected: { zIndex: 2, backgroundColor: '{colors.green.100}' },
      checked: { zIndex: 2, backgroundColor: 'blue.50' },
      default: { zIndex: 2, backgroundColor: 'white' },
    },
  },
  defaultVariants: { highlight: 'default' },
});

const bodyCellRecipe = cva({
  base: {
    border: 'none',
    outline: '1px solid #e2e2e2',
    outlineOffset: '-1px',
  },
  variants: {
    highlight: {
      selected: { backgroundColor: '{colors.green.100}' },
      checked: { backgroundColor: 'blue.50' },
      default: { backgroundColor: 'white' },
    },
    stickyLeft: {
      true: { position: 'sticky', left: '57px', zIndex: 2, boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' },
    },
  },
  defaultVariants: { highlight: 'default' },
});

const rowRecipe = cva({
  variants: {
    checked: {
      true: { backgroundColor: 'blue.50' },
    },
  },
});

const downloadExcel = async (fileName: string, workbook: ExcelJS.Workbook) => {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  window.URL.revokeObjectURL(url);
};

export const MarketCompsTable = ({
  comps,
  isLoading,
  selectedProperty,
  checkedCompIds,
  onToggleComp,
  viewsRankMap,
  leadsRankMap,
  priceHistoryMap,
  isLoadingPriceHistory,
}: MarketCompsTableProps) => {
  const selectedPropertyName = selectedProperty.propertyDetails.listingName;
  const [sortField, setSortField] = useState<string | null>('overlapPerc');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const { selectedPropertyComp, competitorComps } = useMemo(() => {
    const selectedListingAlias = selectedProperty.propertyDetails.listingAlias;
    const selected = comps.find((comp) => comp.listingAlias === selectedListingAlias);
    const competitors = comps.filter((comp) => comp.listingAlias !== selectedListingAlias);
    return { selectedPropertyComp: selected, competitorComps: competitors };
  }, [comps, selectedProperty.propertyDetails.listingAlias]);

  const isOffMarket = selectedProperty.currentPublishedStatus === 'Inactive';
  const tableConfig = useMemo(
    () => makeTableConfig(viewsRankMap, leadsRankMap, isOffMarket, priceHistoryMap, isLoadingPriceHistory),
    [viewsRankMap, leadsRankMap, isOffMarket, priceHistoryMap, isLoadingPriceHistory],
  );

  const sortedComps = useMemo(() => {
    if (!sortField) return competitorComps;

    return [...competitorComps].sort((a, b) => {
      let aValue: unknown;
      let bValue: unknown;

      // For views/leads rank, use the calculated relative rank from the map
      // Unchecked rows will have undefined rank (not in the map)
      if (sortField === 'viewsRank') {
        aValue = viewsRankMap.get(a.listingAlias);
        bValue = viewsRankMap.get(b.listingAlias);
      } else if (sortField === 'leadsRank') {
        aValue = leadsRankMap.get(a.listingAlias);
        bValue = leadsRankMap.get(b.listingAlias);
      } else {
        aValue = a[sortField as keyof ListingCompDto];
        bValue = b[sortField as keyof ListingCompDto];
      }

      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      let comparison = 0;
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue;
      } else {
        comparison = String(aValue).localeCompare(String(bValue));
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [competitorComps, sortField, sortDirection, viewsRankMap, leadsRankMap]);

  const handleDownloadExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Market Comps');

    const headers = tableConfig.map((col) => col.header);
    worksheet.addRow(headers);

    // Add selected property row first
    if (selectedPropertyComp) {
      const selectedPropertyRow = tableConfig.map((col) => {
        const value = selectedPropertyComp[col.field as keyof ListingCompDto];
        const render = col.csvRenderer ?? ((val: unknown) => val);
        return render(value, selectedPropertyComp, true);
      });
      worksheet.addRow(selectedPropertyRow);
    }

    // Add competitor comps
    sortedComps.forEach((comp) => {
      const row = tableConfig.map((col) => {
        const value = comp[col.field as keyof ListingCompDto];
        const render = col.csvRenderer ?? ((val: unknown) => val);
        return render(value, comp, false);
      });
      worksheet.addRow(row);
    });

    const fileName = `market-comps-${selectedPropertyName.replace(/\s+/g, '-')}-${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
    await downloadExcel(fileName, workbook);
  };

  if (isLoading) {
    return (
      <Card
        css={{ padding: '800', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}
      >
        <Spinner size="lg" />
      </Card>
    );
  }

  if (!comps || comps.length === 0) {
    return (
      <Card css={{ padding: '800' }}>
        <Heading level={5}>No competitors found</Heading>
        <Paragraph css={{ marginTop: '400' }}>
          No competitor properties found within the search radius for this property.
        </Paragraph>
      </Card>
    );
  }

  return (
    <Card css={{ padding: '600', display: 'block', width: 0, minWidth: '100%' }}>
      <Box css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '600' }}>
        <Box>
          <Heading level={5}>Competitor Properties</Heading>
          <Paragraph css={{ marginTop: '200', fontSize: '14px' }}>
            Your property + {competitorComps.length} competitor{competitorComps.length !== 1 ? 's' : ''}
          </Paragraph>
        </Box>
        <IconButton
          emphasis="outlined"
          shape="square"
          size="sm"
          onClick={handleDownloadExcel}
          title="Download Excel"
          css={{ minWidth: '32px', minHeight: '32px' }}
        >
          <IconDownloadFilled />
        </IconButton>
      </Box>

      <Box css={{ overflow: 'auto', maxHeight: '70vh' }}>
        <Table.Root aria-label="Market comps table" css={{ borderCollapse: 'separate', borderSpacing: 0 }}>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell className={checkboxCellRecipe({ highlight: 'header' })}> </Table.HeaderCell>
              {tableConfig.map((col) => {
                const isSortable = col.field !== 'listingAlias';
                return (
                  <Table.HeaderCell
                    key={String(col.field)}
                    className={headerCellRecipe({
                      sortable: isSortable,
                      stickyLeft: col.field === 'propertyName',
                    })}
                    css={{ textAlign: col.align || 'left', minWidth: col.width, whiteSpace: 'nowrap' }}
                    onClick={isSortable ? () => handleSort(col.field) : undefined}
                  >
                    <Box css={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      {col.headerTooltip ? (
                        <TooltipHeader label={col.header} tooltip={col.headerTooltip} />
                      ) : (
                        <span>{col.header}</span>
                      )}
                      {isSortable && sortField === col.field && (
                        <span style={{ fontSize: '12px' }}>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </Box>
                  </Table.HeaderCell>
                );
              })}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {selectedPropertyComp && (
              <Table.Row>
                <Table.Cell className={checkboxCellRecipe({ highlight: 'selected' })}>
                  <Checkbox checked disabled />
                </Table.Cell>
                {tableConfig.map((col) => (
                  <Table.Cell
                    key={String(col.field)}
                    className={bodyCellRecipe({
                      highlight: 'selected',
                      stickyLeft: col.field === 'propertyName',
                    })}
                    css={{ textAlign: col.align || 'left', whiteSpace: 'nowrap', ...col.cellCss }}
                  >
                    {col.renderer(selectedPropertyComp[col.field as keyof ListingCompDto], selectedPropertyComp, true)}
                  </Table.Cell>
                ))}
              </Table.Row>
            )}
            {sortedComps.map((comp, index) => {
              const isChecked = checkedCompIds.has(comp.listingAlias);
              return (
                <Table.Row key={`${comp.listingAlias}-${index}`} className={rowRecipe({ checked: isChecked })}>
                  <Table.Cell className={checkboxCellRecipe({ highlight: isChecked ? 'checked' : 'default' })}>
                    <Checkbox checked={isChecked} onChange={() => onToggleComp(comp.listingAlias, false)} />
                  </Table.Cell>
                  {tableConfig.map((col) => (
                    <Table.Cell
                      key={String(col.field)}
                      className={bodyCellRecipe({
                        highlight: isChecked ? 'checked' : 'default',
                        stickyLeft: col.field === 'propertyName',
                      })}
                      css={{ textAlign: col.align || 'left', whiteSpace: 'nowrap', ...col.cellCss }}
                    >
                      {col.renderer(comp[col.field as keyof ListingCompDto], comp, false)}
                    </Table.Cell>
                  ))}
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table.Root>
      </Box>
    </Card>
  );
};
